<template>
  <div class="lp-page terms-page">
    <Navbar />

    <!-- Hero -->
    <section class="lp-hero">
      <div class="lp-container">
        <span class="lp-eyebrow">{{ hero.eyebrow }}</span>
        <h1 class="lp-title">{{ hero.title }}</h1>
        <p class="lp-lead">{{ hero.lead }}</p>

        <div class="terms-meta">
          <span class="terms-updated">{{ effective }}</span>
        </div>
      </div>
    </section>

    <!-- Terms -->
    <section class="lp-section-sm terms-section">
      <div class="lp-narrow">
        <article class="terms-card">
          <section
            v-for="(item, index) in sections"
            :key="item.title"
            class="terms-block"
          >
            <h2 class="terms-h2">
              <span class="terms-num">{{ index + 1 }}</span>
              {{ item.title }}
            </h2>

            <template v-for="(block, i) in item.body" :key="i">
              <div v-if="block.highlight" class="terms-highlight">
                <p v-for="(paragraph, j) in block.highlight" :key="j" v-html="paragraph"></p>
              </div>
              <ul v-else-if="block.list" class="terms-list">
                <li v-for="(entry, j) in block.list" :key="j">{{ entry }}</li>
              </ul>
              <p v-else class="terms-text" v-html="block"></p>
            </template>
          </section>
        </article>
      </div>
    </section>

    <Footer />
  </div>
</template>

<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import Navbar from '../components/Navbar.vue';
import Footer from '../components/Footer.vue';
import { EFFECTIVE_DATE, HERO, SECTIONS } from '../data/terms';

const { locale } = useI18n();

// Follows the site's own language picker — this page has no switch of its own.
// Anything outside the five translated locales reads the English text.
const lang = computed(() => {
  const code = String(locale.value || '').slice(0, 2).toLowerCase();
  return Object.prototype.hasOwnProperty.call(SECTIONS, code) ? code : 'en';
});

const hero = computed(() => HERO[lang.value]);
const sections = computed(() => SECTIONS[lang.value]);
const effective = computed(() => hero.value.effective(EFFECTIVE_DATE[lang.value]));
</script>

<style scoped>
.terms-meta {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 14px;
  flex-wrap: wrap;
  margin-top: 24px;
}

.terms-updated {
  padding: 8px 18px;
  border-radius: var(--pill);
  background: var(--card);
  border: 1px solid var(--line);
  font-size: 13.5px;
  color: var(--muted);
}

.terms-section { padding-bottom: 80px; }

.terms-card {
  background: var(--card);
  border: 1px solid var(--line);
  border-radius: var(--radius);
  box-shadow: var(--shadow);
  padding: 48px 52px;
}

.terms-block { padding-top: 34px; }
.terms-block:first-child { padding-top: 0; }
.terms-block + .terms-block { border-top: 1px solid var(--line); margin-top: 34px; }

.terms-h2 {
  font-size: 21px;
  font-weight: 700;
  display: flex;
  align-items: baseline;
  gap: 12px;
  margin-bottom: 14px;
}

.terms-num {
  flex-shrink: 0;
  font-size: 13px;
  font-weight: 700;
  color: var(--violet);
  background: rgba(43, 130, 240, 0.09);
  border-radius: 8px;
  padding: 3px 9px;
  letter-spacing: 0;
}

.terms-text {
  color: var(--muted);
  line-height: 1.65;
  font-size: 15.5px;
  margin-bottom: 14px;
}
.terms-text:last-child { margin-bottom: 0; }

.terms-list {
  margin: 0 0 14px;
  padding-left: 20px;
  color: var(--muted);
  line-height: 1.65;
  font-size: 15.5px;
}
.terms-list li { margin-bottom: 7px; }
.terms-list li:last-child { margin-bottom: 0; }

/* Pulls the retention promise out of the wall of text. */
.terms-highlight {
  background: rgba(43, 130, 240, 0.06);
  border: 1px solid rgba(43, 130, 240, 0.18);
  border-radius: var(--radius-sm);
  padding: 22px 24px;
  margin-bottom: 14px;
}
.terms-highlight p {
  color: var(--ink);
  line-height: 1.65;
  font-size: 15.5px;
  margin-bottom: 12px;
}
.terms-highlight p:last-child { margin-bottom: 0; }

.terms-card :deep(strong) { color: var(--ink); font-weight: 600; }
.terms-card :deep(a) { color: var(--violet); }
.terms-card :deep(a:hover) { text-decoration: underline; }

@media (max-width: 720px) {
  .terms-card { padding: 32px 24px; border-radius: var(--radius-sm); }
  .terms-h2 { font-size: 19px; }
}
</style>
